import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Mail, Loader2 } from 'lucide-react';
import MarkdownModal from '../components/common/MarkdownModal';
import PasswordInput from '../components/common/PasswordInput';
import AuthLayout from '../components/common/AuthLayout';
import { showSuccessToast, showErrorToast } from '../lib/toast';

const isEmailNotConfirmedError = (err, msg) =>
  err?.code === 'email_not_confirmed' || /email not confirmed/i.test(msg || '');

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const { login, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const confirmedToastShown = useRef(false);

  useEffect(() => {
    const prefilledEmail = location.state?.email;
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
    if (location.state?.existingAccount) {
      setError('You already have an account. Log in to continue.');
    }
  }, [location.state?.email, location.state?.existingAccount]);

  useEffect(() => {
    if (searchParams.get('confirmed') === '1' && !confirmedToastShown.current) {
      confirmedToastShown.current = true;
      showSuccessToast('Email confirmed. You can now log in.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsConfirmation(false);
    setIsLoading(true);

    try {
      await login(email, password);
      showSuccessToast('Welcome back.');
      navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailString = typeof detail === 'string' ? detail : null;
      const msg = detailString || err?.message || 'Failed to login. Please check your credentials.';

      if (isEmailNotConfirmedError(err, msg)) {
        setNeedsConfirmation(true);
        setError('Please confirm your email before logging in.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      showErrorToast('Enter your email above first.');
      return;
    }
    setResendLoading(true);
    try {
      await resendConfirmation(email);
      showSuccessToast('Confirmation email sent.');
    } catch (err) {
      showErrorToast(err?.message || 'Could not resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const footer = (
    <p className="text-center text-[13px] text-gray-400 dark:text-gray-500">
      <button
        onClick={() => setTermsModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Terms
      </button>
      {' · '}
      <button
        onClick={() => setPrivacyModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Privacy
      </button>
    </p>
  );

  return (
    <AuthLayout footer={footer}>
      <div className="space-y-2">
        <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Welcome back</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Sign in to explore Earth data</p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl animate-in slide-in-from-top-2 duration-200">
              <div>{error}</div>
              {needsConfirmation && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 underline-offset-4 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={14} />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Resend confirmation email</span>
                  )}
                </button>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <Mail size={18} />
              </div>
              <input
                type="email"
                required
                className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
            >
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogIn size={20} className="group-hover:translate-x-0.5 transition-transform" />
                <span>Sign In</span>
              </>
            )}
          </button>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors underline-offset-4 hover:underline"
              >
                Get started
              </Link>
            </p>
          </div>
      </form>

      <MarkdownModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        markdownUrl="/terms-of-service.md"
      />

      <MarkdownModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        markdownUrl="/privacy-policy.md"
      />
    </AuthLayout>
  );
};

export default LoginPage;
